import React, { useEffect, useState, useContext } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { GlobalContext } from '../GlobalContext'
import FooterNavButtons from '../shared-components/FooterNavButtons'
import HeaderDetails from '../shared-components/HeaderDetails'
import RedAlert from '../ui-elements/RedAlert'
import Spinner from '../ui-elements/Spinner'
import { getChecklistProgress } from '../utils/getChecklistProgress'
import ChecklistStatus from './ChecklistStatus'
import LoopThroughQuestions from './LoopThroughQuestions'
import FormatCheckPeriodInProgress from './FormatCheckPeriodInProgress'
import { useVoice } from '../context/voice/VoiceContext'
import { useActivityBroadcast } from '../context/activity/ActivityBroadcastContext'


type Props = {
}

function ChecklistQuestions({
}: Props) {

  const {
    tableData,
    isSignoffRequired,
    checkIfThereAreQuestionsAndAnswersForChecklistPeriodId,
    t
  } = useContext(GlobalContext)


  const { checkPeriodId } = useParams()
  const navigate = useNavigate()
  const [checklistProgress, setChecklistProgress] = useState<null | ObjectStringKeyAnyValue>(null)
  const [nextButtonText, setNextButtonText] = useState('')
  const [nextButtonUrl, setNextButtonUrl] = useState<null | string>(null)


  const thisCheckPeriod = checkPeriodId && tableData && tableData.ChecklistPeriods && tableData.ChecklistPeriods[checkPeriodId]
  const thisChecklistId = thisCheckPeriod && thisCheckPeriod['ChecklistId'] ? thisCheckPeriod['ChecklistId'] : null
  const thisCheckPeriodSignoff = thisCheckPeriod && thisCheckPeriod['Signoff'] ? thisCheckPeriod['Signoff'] : null
  const processIdForThisChecklist = thisCheckPeriod && thisCheckPeriod.ProcessDefinitionId ? thisCheckPeriod.ProcessDefinitionId : null
  const processRecordIdForThisChecklist = thisCheckPeriod && thisCheckPeriod.ProcessRecordId ? thisCheckPeriod.ProcessRecordId : null
  const thisChecklist = thisChecklistId && tableData && tableData.Checklists && tableData.Checklists[thisChecklistId]
  const processUrl = processIdForThisChecklist
    ? (processRecordIdForThisChecklist ? `/process/${processIdForThisChecklist}/record/${processRecordIdForThisChecklist}` : `/process/${processIdForThisChecklist}`)
    : null


  let titleText = thisChecklist && thisChecklist['ChecklistName'] ? thisChecklist['ChecklistName'] : 'Checklist'
  let assetId = null
  let asset_name = null
  if (thisChecklist && thisCheckPeriod && thisCheckPeriod['AssetId']) {
    assetId = thisCheckPeriod['AssetId']
    if (tableData && tableData['Assets'] && tableData['Assets'][thisCheckPeriod['AssetId']]) {
      asset_name = tableData['Assets'][thisCheckPeriod['AssetId']]['AssetName']
    } else {
      console.log("asset name not found for asset id", thisCheckPeriod['AssetId'])
    }
    titleText = `${titleText} for ${asset_name || assetId}`

  }





  useEffect(() => {
    checkIfThereAreQuestionsAndAnswersForChecklistPeriodId(checkPeriodId)
  }, [])


  const { broadcast } = useActivityBroadcast()
  useEffect(() => {
    if (!checkPeriodId) return
    broadcast('isAnsweringChecklist', { checkPeriodId })
    return () => {
      // drilling into a question of this check period is not "leaving" the checklist
      if (window.location.pathname.startsWith(`/checklists/${checkPeriodId}/`)) return
      broadcast('noLongerAnsweringChecklist', { checkPeriodId })
    }
  }, [checkPeriodId])


  useEffect(() => {
    //console.log(`🚨 running update`)
    if (checkPeriodId && tableData) {
      const progress = getChecklistProgress(checkPeriodId, tableData)
      if (progress) {
        setChecklistProgress(progress)
      }

    }
  }, [checkPeriodId, tableData])




  useEffect(() => {
    setNavButtons()
  }, [checklistProgress, isSignoffRequired, thisCheckPeriodSignoff])





  const setNavButtons = () => {
    // if checklist isn't complete, show scan qr code
    if (
      checklistProgress &&
      checklistProgress.numberOfExpectedAnswers < checklistProgress.numberOfCompletedAnswers) {
      setNextButtonText(t('Buttons.scanQRcode'))
      setNextButtonUrl(`/checklists/${checkPeriodId}/scan-qr-code`)
    }
    // if checklist is complete and signoff is required but not done yet,
    // show complete checklist
    else if (
      isSignoffRequired &&
      !thisCheckPeriodSignoff &&
      checklistProgress &&
      (checklistProgress.numberOfExpectedAnswers === checklistProgress.numberOfCompletedAnswers)) {
      setNextButtonText(t('Buttons.signOff'))
      setNextButtonUrl(`/checklists/${checkPeriodId}/complete-checklist`)
    }
  }



  return <div className={`w-full flex-1 h-full flex flex-col justify-between gap-2`}>



    <div className={`w-full flex flex-col items-center`}>
      <HeaderDetails
        titleText={titleText}
        subheadingText={assetId}
      />






      <div className={`max-w-3xl w-full p-5 flex flex-col gap-2 items-center justify-center text-center`}>

        {(!tableData || !checkPeriodId) && <Spinner><p>{t('General.loading')}</p></Spinner>}


        {tableData && checkPeriodId && tableData.ChecklistPeriods && tableData.Checklists && (!thisCheckPeriod || !thisChecklist) && <RedAlert><div className={`w-full flex flex-col items-center`}>
          {!thisCheckPeriod && <p>{t('Dashboard.ChecklistQuestions.checkPeriodNotFound', { id: checkPeriodId })}</p>}
          {!thisChecklist && <p>{t('Dashboard.ChecklistQuestions.checklistIdNotFound', { id: thisChecklistId })}</p>}
        </div></RedAlert>}



        <ChecklistStatus
          checkPeriodId={checkPeriodId}
          checklistProgress={thisCheckPeriod && thisCheckPeriod.Progress}
          thisCheckPeriodSignoff={thisCheckPeriodSignoff}
          thisChecklistPeriod={thisCheckPeriod}
          thisChecklist={thisChecklist}
        />

        {thisChecklist && checkPeriodId && <LoopThroughQuestions
          checkPeriodId={checkPeriodId}
          thisChecklist={thisChecklist}
        />}

      </div>
    </div>

    <FooterNavButtons
      backButtonUrl={processUrl || `/`}
      // flag the return so ProcessSummary sends a now-finished process to its complete page
      backButtonAction={processUrl ? () => navigate(processUrl, { state: { fromChecklist: true } }) : null}
      backButtonText={'Back'}
      nextButtonUrl={nextButtonUrl}
      nextButtonText={nextButtonText}
    />

  </div>
}

export default ChecklistQuestions